import { api, unwrap, type ResponseEnvelope } from '@saas-kit/lib';

import type {
  ListEnvelope,
  QmsDisplay,
  QmsDisplayLayout,
  QmsDisplayMedia,
  QmsDisplayPublic,
  QmsDisplayWithToken,
} from './types';

export interface CreateDisplayInput {
  slug: string;
  name: string;
  layout: QmsDisplayLayout;
  is_active: boolean;
}

export async function listDisplays(): Promise<QmsDisplay[]> {
  const data = await unwrap<ListEnvelope<QmsDisplay>>(api.get('/qms/displays'));
  return data.items;
}

export async function createDisplay(
  input: CreateDisplayInput,
): Promise<QmsDisplayWithToken> {
  return unwrap<QmsDisplayWithToken>(api.post('/qms/displays', input));
}

export async function updateDisplay(
  id: string,
  input: Record<string, unknown>,
): Promise<QmsDisplay> {
  return unwrap<QmsDisplay>(api.patch(`/qms/displays/${id}`, input));
}

export async function rotateDisplayToken(
  id: string,
): Promise<QmsDisplayWithToken> {
  return unwrap<QmsDisplayWithToken>(
    api.post(`/qms/displays/${id}/rotate_token`, {}),
  );
}

export async function deleteDisplay(id: string): Promise<void> {
  await unwrap(api.delete<ResponseEnvelope<unknown>>(`/qms/displays/${id}`));
}

/** A media upload can be many MB over a slow uplink, so it must not be
 *  bound by the api client's 15s default timeout — that aborts the XHR
 *  mid-stream and the proxy logs a 499. Allow up to 10 minutes. */
const MEDIA_UPLOAD_TIMEOUT_MS = 600_000;

/** Upload an image/video for a display's branding panel.
 *  No explicit Content-Type — the browser must set
 *  `multipart/form-data; boundary=…`; hard-coding it without the
 *  boundary makes the request body unparseable server-side. */
export async function uploadDisplayMedia(
  file: File,
): Promise<QmsDisplayMedia> {
  const body = new FormData();
  body.append('uploaded_file', file);
  return unwrap<QmsDisplayMedia>(
    api.post('/qms/displays/media', body, {
      timeout: MEDIA_UPLOAD_TIMEOUT_MS,
    }),
  );
}

/** Resolve a fresh download URL for a previously uploaded media file. */
export async function fetchDisplayMedia(
  fileId: string,
): Promise<QmsDisplayMedia> {
  return unwrap<QmsDisplayMedia>(api.get(`/qms/displays/media/${fileId}`));
}

/** Public, token-authenticated board feed — no tenant session required. */
export async function fetchPublicDisplay(
  slug: string,
  token: string,
): Promise<QmsDisplayPublic> {
  return unwrap<QmsDisplayPublic>(
    api.get(`/qms/public/displays/${encodeURIComponent(slug)}`, {
      params: { t: token },
    }),
  );
}
